import { Check, Copy, Download, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'

type MfaRecoveryCodesDialogProps = {
  codes: string[] | null
  onClose: () => void
}

export function MfaRecoveryCodesDialog({ codes, onClose }: MfaRecoveryCodesDialogProps) {
  const [copied, setCopied] = useState(false)
  const [downloaded, setDownloaded] = useState(false)

  const copyCodes = async () => {
    if (!codes) return
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopied(true)
      toast.success('Códigos copiados.')
    } catch {
      toast.error('No se pudieron copiar. Guárdalos manualmente antes de cerrar.')
    }
  }

  const downloadCodes = () => {
    if (!codes) return

    const generatedAt = new Intl.DateTimeFormat('es-GT', {
      dateStyle: 'long',
      timeStyle: 'short'
    }).format(new Date())
    const contents = [
      'IDC Forms — códigos de recuperación',
      `Generados: ${generatedAt}`,
      '',
      'Cada código funciona una sola vez. Guárdalos en un lugar seguro y no los compartas.',
      'Elimina este archivo del equipo después de moverlo a tu gestor de contraseñas o almacenamiento seguro.',
      '',
      ...codes,
      ''
    ].join('\n')

    try {
      const blob = new Blob(['\uFEFF', contents], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `idc-forms-codigos-recuperacion-${new Date().toISOString().slice(0, 10)}.txt`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 0)
      setDownloaded(true)
      toast.success('Archivo TXT descargado.')
    } catch {
      toast.error('No se pudo descargar el archivo. Copia los códigos antes de cerrar.')
    }
  }

  return (
    <Dialog
      open={codes !== null}
      onOpenChange={(open) => {
        if (!open) {
          setCopied(false)
          setDownloaded(false)
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Guarda tus códigos de recuperación</DialogTitle>
          <DialogDescription>
            Cada código funciona una sola vez si pierdes acceso a tu aplicación de autenticación. No
            volveremos a mostrarlos.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          <ShieldAlert className="mt-0.5 size-5 shrink-0" />
          <p className="text-sm">Guárdalos fuera de IDC Forms, idealmente en un gestor seguro.</p>
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/40 p-4 font-mono text-sm">
          {(codes ?? []).map((code) => (
            <span key={code}>{code}</span>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={copyCodes}>
            {copied ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
            {copied ? 'Copiados' : 'Copiar todos'}
          </Button>
          <Button type="button" variant="outline" onClick={downloadCodes}>
            {downloaded ? <Check className="mr-2 size-4" /> : <Download className="mr-2 size-4" />}
            {downloaded ? 'TXT descargado' : 'Descargar TXT'}
          </Button>
          <Button type="button" onClick={onClose}>
            Ya los guardé
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
